// Page-level browser notifications for check-ins (architecture sprint-04 §5.5, §6.4). No service
// worker, no push. Every helper is feature-detected and never throws.

export type PermissionState = NotificationPermission | 'unsupported';

export function notificationsSupported(): boolean {
  return typeof Notification !== 'undefined';
}

export function notificationPermission(): PermissionState {
  if (!notificationsSupported()) return 'unsupported';
  try {
    return Notification.permission;
  } catch {
    return 'unsupported';
  }
}

export async function requestNotificationPermission(): Promise<PermissionState> {
  if (!notificationsSupported()) return 'unsupported';
  try {
    return await Notification.requestPermission();
  } catch {
    return notificationPermission();
  }
}

/** The tag is the slotId, so several hidden tabs firing for one slot collapse to one OS notification. */
export function showCheckinNotification(slotId: string, label: string): void {
  if (notificationPermission() !== 'granted') return;
  try {
    const n = new Notification('Still working?', { body: `Still on ${label}?`, tag: slotId });
    n.onclick = (): void => {
      window.focus();
      n.close();
    };
  } catch {
    // Some browsers (Android Chrome) only allow notifications from a service worker.
  }
}

/** Sprint 14 (§8.2): the check-in alarm. One OS notification per mark across tabs (tag); stays until clicked. */
export function showAlarmNotification(tag: string, dueBy: string): void {
  if (notificationPermission() !== 'granted') return;
  try {
    const n = new Notification('Clockit check-in', { body: `Tap "I'm awake" to confirm. Due by ${dueBy}.`, tag, requireInteraction: true });
    n.onclick = (): void => {
      window.focus();
      n.close();
    };
  } catch {
    // Page-level notifications are not available here (for example Android Chrome).
  }
}

/** Sprint 14 (§8.5): a Supervisor's missed check-in alert. The tag is the event id. */
export function showAlertNotification(eventId: string, title: string, body: string): void {
  if (notificationPermission() !== 'granted') return;
  try {
    const n = new Notification(title, { body, tag: eventId, requireInteraction: true });
    n.onclick = (): void => {
      window.focus();
      n.close();
    };
  } catch {
    // ignore
  }
}

/** Sprint 15 (§9.2): a shift's notes to review. The tag is the event id; a click focuses and opens the shift. */
export function showReviewNotification(eventId: string, title: string, body: string, onOpen: () => void): void {
  if (notificationPermission() !== 'granted') return;
  try {
    const n = new Notification(title, { body, tag: eventId });
    n.onclick = (): void => {
      window.focus();
      onOpen();
      n.close();
    };
  } catch {
    // Page-level notifications are not available here (for example Android Chrome).
  }
}
